// What both layouts share about DMs: their avatar, which ones are listed and in what order, the header of an open DM
// and how the UI names one. (The Classic sidebar's list of DMs is components/classic/ClassicDmList.tsx.)
import { decodeTime } from "ulid";
import { dmHidden, dmOthers, dmTitle, type Channel } from "@bloom/shared";
import type { ChannelState } from "../realtime.ts";
import { Avatar } from "./ui.tsx";
import "./dms.css";

/** One face for a 1:1 DM; two overlapping faces for a group. */
export function DmAvatar({ channel, me, names, size, online }: { channel: Channel; me: string | null; names: Map<string, string>; size: number; online?: Set<string> }) {
  const others = dmOthers(channel, me);
  if (others.length <= 1) {
    const id = others[0] ?? me ?? "";
    return <Avatar id={id} name={names.get(id) ?? "?"} size={size} presence={online?.has(id) ? "online" : undefined} />;
  }
  const small = Math.round(size * 0.72);
  return (
    <span className="dm-stack" style={{ width: size, height: size }} aria-hidden="true">
      <Avatar id={others[0]!} name={names.get(others[0]!) ?? "?"} size={small} />
      <Avatar id={others[1]!} name={names.get(others[1]!) ?? "?"} size={small} />
    </span>
  );
}

/** When a DM last saw a message: the newest one we hold, else when it was created (its id is a ULID). */
/**
 * The DMs the sidebar lists, most recently active first. A DM someone opened but nobody wrote in yet stays out of
 * the list, and so does one I closed until its next message (#131); either shows while it's open here. Alt+↑/↓ and
 * the quick switcher use the same order.
 */
export function shownDms(dms: Channel[], byChannel: Record<string, ChannelState>, activeId: string | undefined, closed: Record<string, number>): Channel[] {
  return dms.filter((c) => (c.lastSeq > 0 || c.id === activeId) && !dmHidden(c.lastSeq, closed[c.id], c.id === activeId)).sort((a, b) => lastActive(b, byChannel[b.id]) - lastActive(a, byChannel[a.id]));
}

function lastActive(c: Channel, state: ChannelState | undefined): number {
  const last = state?.messages.at(-1);
  if (last) return last.ts;
  try {
    return decodeTime(c.id);
  } catch {
    return 0;
  }
}

/** The chat header for a DM: the other people, "@Maya" for one, "Maya, Sam" for a group. */
export function DmHeader({ channel, me, names, online }: { channel: Channel; me: string | null; names: Map<string, string>; online: Set<string> }) {
  const others = dmOthers(channel, me);
  const title = dmTitle(channel, me, names);
  return (
    <h1 className="channel-title dm-title">
      <DmAvatar channel={channel} me={me} names={names} size={22} online={online} />
      <span className="dm-name">{others.length === 1 ? `@${title}` : title}</span>
    </h1>
  );
}

/** How the composer and other bits of UI refer to a DM: "@Maya", or "Maya, Sam" for a group. */
export function dmLabel(channel: Channel, me: string | null, names: Map<string, string>): string {
  const title = dmTitle(channel, me, names);
  return dmOthers(channel, me).length === 1 ? `@${title}` : title;
}
